import type { DestinationId } from './home-page-config';

export type FeatureConfig = {
  savedRequests: boolean;
  premiumContent: boolean;
  songsToLearn: boolean;
  tokens: boolean;
  collabSessions: boolean;
  duration: boolean;
  globalSongSearch: boolean;
};

export const DEFAULT_FEATURE_CONFIG: FeatureConfig = {
  savedRequests: true,
  premiumContent: true,
  songsToLearn: true,
  tokens: true,
  collabSessions: true,
  duration: false,
  globalSongSearch: false,
};

// Each feature defaults to enabled: only an explicit `false` disables it, so
// streamers with no stored config keep every feature.
//
// Duration inverts that rule. Song lengths are blank unless a streamer records
// them, and empty progress bars, a column of dashes and a queue ETA built on
// missing data are worse than showing nothing — so it takes an explicit `true`.
//
// Global song search inverts it too, for a different reason: it publishes a
// streamer's list outside their own page, and that is not a default anyone
// should arrive at by omission.
export function resolveFeatureConfig(raw: unknown): FeatureConfig {
  if (raw == null || typeof raw !== 'object') return DEFAULT_FEATURE_CONFIG;
  const obj = raw as Record<string, unknown>;
  return {
    savedRequests: obj.savedRequests !== false,
    premiumContent: obj.premiumContent !== false,
    songsToLearn: obj.songsToLearn !== false,
    tokens: obj.tokens !== false,
    collabSessions: obj.collabSessions !== false,
    duration: obj.duration === true,
    globalSongSearch: obj.globalSongSearch === true,
  };
}

// Home page destination tiles that a feature toggle gates. A disabled feature
// force-hides its tile regardless of the per-tile `visible` flag.
export const FEATURE_DESTINATIONS: Partial<Record<DestinationId, keyof FeatureConfig>> = {
  'saved-requests': 'savedRequests',
  premium: 'premiumContent',
};

export function isDestinationFeatureEnabled(
  config: FeatureConfig,
  destinationId: DestinationId,
): boolean {
  const feature = FEATURE_DESTINATIONS[destinationId];
  return feature ? config[feature] : true;
}
